import { useEffect, useRef, useState } from "react";

import { CoreService } from "@/services/core/CoreService";
import { AnalyticsService } from "@/services/features/AnalyticsService";
import { AnalyticsServiceV2, PopularItemsParams } from "@/services/features/AnalyticsServiceV2";
import InventoryService, {
  type InventoryItem,
} from "@/services/features/inventory/InventoryService";
import { storePerformanceService } from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";

interface TriedOnItemData {
  sku: string;
  title: string;
  imageUrl: string;
  size: string;
  triedOnTimes: number;
  numberSold: number;
  quantityInStock: number;
  loading: boolean;
}

interface UsePopularTriedOnItemsReturn {
  items: TriedOnItemData[];
  loading: boolean;
  error: string | null;
}

export const usePopularTriedOnItems = (
  storeId: number | null,
  dateRange: { start: Date; end: Date },
  areaIds?: number[],
): UsePopularTriedOnItemsReturn => {
  const browserTimezone = getBrowserTimezone();
  const areaIdsKey = areaIds?.join(",") ?? "";
  const [items, setItems] = useState<TriedOnItemData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);
  const requestKey =
    storeId && dateRange.start && dateRange.end
      ? `${storeId}:${dateRange.start.getTime()}:${dateRange.end.getTime()}:${browserTimezone}:${areaIdsKey}`
      : null;
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);

  useEffect(() => {
    activeRequestIdRef.current += 1;

    if (!storeId || !dateRange.start || !dateRange.end) {
      console.log("Missing required parameters, skipping fetch");
      setItems([]);
      setLoading(false);
      setCompletedRequestKey(null);
      return;
    }

    const requestId = activeRequestIdRef.current;
    let cancelled = false;
    const isActive = () => !cancelled && activeRequestIdRef.current === requestId;

    const fetchPopularItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const analyticsLegacyService = AnalyticsService.getInstance();
        const coreService = CoreService.getInstance();
        const token = coreService.getAuthorizationHeader();

        if (!token) {
          if (!isActive()) return;
          setItems([]);
          return;
        }

        const inventoryService = new InventoryService(token);

        const startDate = storePerformanceService.formatDate(dateRange.start, browserTimezone);
        const endDate = storePerformanceService.formatDate(dateRange.end, browserTimezone);
        const popularItemsParams: PopularItemsParams = {
          storeId,
          startDate,
          endDate,
          limit: 10,
          ...(areaIdsKey ? { areaIds: areaIdsKey.split(",").map(Number) } : {}),
        };

        const popularItemsResponse = await analyticsService.getPopularItems(popularItemsParams);

        if (!isActive()) return;

        if (!popularItemsResponse.data || popularItemsResponse.data.length === 0) {
          setItems([]);
          return;
        }

        const popularItems = popularItemsResponse.data;

        // Initialize items with loading state
        const initialItems: TriedOnItemData[] = popularItems.map((item) => ({
          sku: item.sku,
          title: "",
          imageUrl: "",
          size: "",
          triedOnTimes: item.count,
          numberSold: 0,
          quantityInStock: 0,
          loading: true,
        }));

        setItems(initialItems);

        const itemsWithDetails = await Promise.all(
          popularItems.map(async (item) => {
            const [inventoryResult, soldSummaryResult] = await Promise.allSettled([
              inventoryService.getSkuBySkuId(storeId, item.sku),
              analyticsLegacyService.getSkuSalesSummary({
                storeIds: [storeId],
                sku: item.sku,
                startDate,
                endDate,
              }),
            ]);

            let title = item.sku;
            let imageUrl = "";
            let size = "";
            let quantityInStock = 0;

            if (inventoryResult.status === "fulfilled") {
              const skuData: InventoryItem = inventoryResult.value;
              const combinedSize =
                skuData.size2 && skuData.size2.trim()
                  ? `${skuData.size || ""} x ${skuData.size2}`.trim()
                  : skuData.size || "";

              title = skuData.title || item.sku;
              imageUrl = skuData.imageUrl || "";
              size = combinedSize;
              quantityInStock = skuData.inStoreQuantity || 0;
            } else {
              console.warn(
                `Failed to fetch inventory details for product ${item.sku}:`,
                inventoryResult.reason,
              );
            }

            const numberSold =
              soldSummaryResult.status === "fulfilled"
                ? soldSummaryResult.value?.data?.numberOfItemsSold || 0
                : 0;

            return {
              sku: item.sku,
              title,
              imageUrl,
              size,
              triedOnTimes: item.count,
              numberSold,
              quantityInStock,
              loading: false,
            };
          }),
        );

        if (!isActive()) return;
        setItems(itemsWithDetails);
      } catch (err) {
        if (!isActive()) return;
        console.error("Failed to fetch popular tried on items:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        setItems([]);
      } finally {
        if (!isActive()) return;
        setLoading(false);
        setCompletedRequestKey(requestKey);
      }
    };

    void fetchPopularItems();

    return () => {
      cancelled = true;
    };
  }, [areaIdsKey, browserTimezone, dateRange.end, dateRange.start, requestKey, storeId]);

  return {
    items,
    loading: requestKey !== null && (loading || completedRequestKey !== requestKey),
    error,
  };
};
